import React, { FC, memo, useCallback, useEffect, useMemo } from 'react'

import { useFocusStoreDangerously } from '@please/lrud'
import { Element } from 'react-scroll'

import { ContentUnavailable } from '@/components/ContentUnavailable/ContentUnavailable'
import { Line } from '@/components/Lines/Line'
import { focusIds, orientation } from '@/constants/focus'
import { ContentUnavailableWrapper } from '@/containers/Search/styles'
import { ScrollContainer } from '@/containers/styles'
import { useCustomFocus } from '@/hooks/useCustomFocus'
import { useCommonTranslation } from '@/i18n/useCommonTranslation'
import { SearchLinesGroupType } from '@/types/sliderTypes'

import { LineLoader } from './LineLoader'
import { LineGroupElem } from './styles'

const LinesGroupRaw: FC<SearchLinesGroupType> = ({
  scrollOptions,
  isSectionTitleHidden,
  focusIdPrefix = '',
  anchorId,
  linesData,
  requestAction,
  loading,
  error,
  dataRequested = false,
}) => {
  const focusId = `${focusIdPrefix}_${focusIds.LINES_GROUP}`

  const lineAnchorIndex = linesData.findIndex((el) => el.sliderId === anchorId)
  const startFocusChild = lineAnchorIndex >= 0 ? lineAnchorIndex : undefined
  const hasData = linesData.some(({ data }) => data.length > 0)

  const { t } = useCommonTranslation()
  const { defaultFocusChild, onMove } = useCustomFocus({
    focusId,
    scrollOptions,
    startFocusChild,
  })

  const updateNode = useFocusStoreDangerously().updateNode

  useEffect(() => {
    updateNode(focusId, {
      disabled: !hasData,
    })
  }, [hasData])

  const renderContent = useCallback(() => {
    if (!hasData && dataRequested) {
      return (
        <ContentUnavailableWrapper>
          <ContentUnavailable>
            {t('search.lines.noDataText')}
          </ContentUnavailable>
        </ContentUnavailableWrapper>
      )
    }

    return linesData?.map((lineData, index) => {
      const lineType = `${lineData.searchResultType}_SearchType`
      const focusId = `${focusIdPrefix}_${focusIds.LINE}_${lineType}_${
        lineData.sliderId
      }_${index + 1}`

      return (
        <div key={focusId}>
          {lineData.data.length > 0 && (
            <Element name={focusId}>
              <Line
                nodeId={focusId}
                lineTitle={lineData.name}
                lineData={lineData}
                isTitleHidden={isSectionTitleHidden}
                isPopup
              />
            </Element>
          )}
        </div>
      )
    })
  }, [hasData, linesData, dataRequested])

  const renderedContent = useMemo(
    () =>
      loading ? (
        <LineLoader error={error} action={requestAction} />
      ) : (
        renderContent()
      ),
    [linesData, loading, requestAction, error],
  )

  return (
    <LineGroupElem
      defaultFocusChild={defaultFocusChild}
      focusId={focusId}
      orientation={orientation.VERTICAL}
      onMove={onMove}
      $totalLines={linesData.length}
    >
      <ScrollContainer
        id={scrollOptions.containerId}
        height={scrollOptions.height}
        $withMargin
      >
        {renderedContent}
      </ScrollContainer>
    </LineGroupElem>
  )
}

export const SearchLinesGroup = memo(LinesGroupRaw)
